import React, { FC, ReactNode, useEffect, useRef, useState } from "react";
import { keyframes } from "@emotion/react";
import { Tag, TagLabel } from "@UI/Components/Tag/Tag";
import { Box } from "@UI/Components/Box/Box";
import { CircleCheck } from "@UI/Components/Icons/CircleCheck";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";

import messages from "../../messages";

const EXIT_DURATION = 600;

const pillBounceIn = keyframes`
  0% { transform: scale(0.55); opacity: 0; }
  55% { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

const pillFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const ENTERING_SX = {
  animation: `${pillBounceIn} 400ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards`,
  "@media (prefers-reduced-motion: reduce)": {
    animation: `${pillFadeIn} 400ms ease forwards`,
  },
};

const EXITING_SX = {
  opacity: 0,
  transform: "scale(0.9)",
  transition: "opacity 600ms ease, transform 600ms ease",
  "@media (prefers-reduced-motion: reduce)": {
    transform: "none",
  },
};

type Props = {
  /** Show (bounce in) or hide (fade out) the pill. */
  visible: boolean;
  /** Bump this on every new "save" event to replay the entrance while already visible. */
  replayToken?: number;
  label?: ReactNode;
};

export const DraftSavedPill: FC<Props> = ({
  visible,
  replayToken = 0,
  label = <FormattedMessage {...messages["taskCreation.summary.draftSaved"]} />,
}) => {
  const [mounted, setMounted] = useState(visible);
  const [exiting, setExiting] = useState(false);
  const exitTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    clearTimeout(exitTimeoutRef.current);

    if (visible) {
      setMounted(true);
      setExiting(false);
      return;
    }

    if (mounted) {
      setExiting(true);
      exitTimeoutRef.current = setTimeout(() => {
        setMounted(false);
        setExiting(false);
      }, EXIT_DURATION);
    }

    return () => clearTimeout(exitTimeoutRef.current);
  }, [visible, replayToken, mounted]);

  if (!mounted) {
    return null;
  }

  return (
    <Tag
      key={replayToken}
      rounded="full"
      size="md"
      bg="seaGreen.50"
      color="seaGreen.700"
      sx={exiting ? EXITING_SX : ENTERING_SX}
    >
      <Box as="span" display="inline-flex" alignItems="center" fontSize="sm">
        <CircleCheck />
      </Box>
      <TagLabel fontSize="sm" ml="1.5" color="seaGreen.700">
        {label}
      </TagLabel>
    </Tag>
  );
};
